import _ from 'lodash';
import {
  alertType,
  arrivalAirportCodeCityStateCountry,
  camelCaseItFilter,
  DepArr,
  departureAirportCodeCityStateCountry,
  Flight,
  flight,
  flightPath,
  flightStatusIndicatorStyle,
  flightTimeLabelStr,
  flightTimeValue,
  formatDateSpacedMDY,
  formatDayOfWeek,
  formatFullDateTime,
  parseAndFormatDateYMD,
} from './filters';
import { ITINERARY_ROUTE } from '../defineTdm';
import { getProperty } from '../fsConfig';
import qs from 'qs';
import { Hop, Leg, Trip } from '../headersegment/interfaces';
import moment from 'moment';

const getYearMonthDay = (time: string) => {
  const filterYearMonthDay = (date: string, withTime: boolean) =>
    parseAndFormatDateYMD(date, withTime);
  const date = filterYearMonthDay(time, false);
  return date.length ? date : null;
};

const extractLastArrivalDate = (trip: Trip): string | undefined => {
  try {
    const leg = trip.legs[trip.legs.length - 1];
    const flight = leg.flights[leg.flights.length - 1]?.flight;
    return flight ? getYearMonthDay(flight.arrivalTime) || undefined : undefined;
  } catch (error) {
    return undefined;
  }
};

const extractFirstDepartureDate = (trip: Trip): string | undefined => {
  try {
    const flight = trip.legs[0].flights[0].flight;
    return flight ? getYearMonthDay(flight.departureTime) || undefined : undefined;
  } catch (error) {
    return undefined;
  }
};

export const filterFormatFullDateTime = (dateString: string) =>
  formatFullDateTime(dateString);

export const setFirstAndLastFlightDates = (
  trip: Trip,
  setTrip: (trip: Trip) => void,
) => {
  setTrip({
    ...trip,
    firstDepartureDate: extractFirstDepartureDate(trip),
    lastArrivalDate: extractLastArrivalDate(trip),
  });
};

export const buildContactsMaps = (
  trip: Trip,
  setContactsMap: (contactsMap: Record<string, any>) => void,
  setAwayContactPointsMap: (
    awayContactPointsMap: Record<string, any[]>,
  ) => void,
) => {
  if (!trip) return;

  const contactsMap: { [key: string]: any } = {};
  const awayContactPointsMap: { [key: string]: any[] } = {};

  if (trip.emailAddresses?.length) {
    trip.emailAddresses.forEach(email => {
      contactsMap[email] = null;
      awayContactPointsMap[email] = [];
    });
  }

  if (trip.travelerProfiles?.length) {
    trip.travelerProfiles.forEach(profile => {
      contactsMap[profile.travelerKey] = {
        ...profile,
        sendAlertsPretty: profile.sendAlerts ? 'Yes' : 'No',
      };

      if (profile.contactPoints) {
        profile.contactPoints.forEach((point: any) => {
          point.alertTypePretty =
            point.alertType?.toUpperCase() === 'HOME'
              ? 'Pre-trip'
              : point.alertType?.toUpperCase() === 'AWAY'
              ? 'Traveling'
              : 'API';

          point.messageTypePretty =
            point.messageType?.toUpperCase() === 'EMAIL'
              ? 'Email'
              : point.messageType?.toUpperCase() === 'TEXT'
              ? 'Text to Cell Phone'
              : '';

          point.formatPretty =
            point.format?.toUpperCase() === 'HTML'
              ? 'HTML'
              : point.format?.toUpperCase() === 'MOBILE'
              ? 'Short Text'
              : point.format;

          point.isActivePretty = camelCaseItFilter(`${point.isActive}`);

          if (
            point.alertType?.toUpperCase() === 'AWAY' &&
            point.isActive &&
            point.contact
          ) {
            if (!awayContactPointsMap[profile.travelerKey]) {
              awayContactPointsMap[profile.travelerKey] = [];
            }
            awayContactPointsMap[profile.travelerKey].push(point);
          }
        });
      }
    });
  }

  setContactsMap(contactsMap);
  setAwayContactPointsMap(awayContactPointsMap);
};

export const filterFlight = (airlineCode: string, flightNumber: string) =>
  flight(airlineCode, flightNumber);
export const filterFlightPath = (
  departureAirportCode: string,
  arrivalAirportCode: string,
) => flightPath(departureAirportCode, arrivalAirportCode);
export const filterAlertType = (alertTypes: string) => alertType(alertTypes);

export const createTripNotificationUrl = (tripId: string, notificationId: string) => {
  return tripId && notificationId
    ? `/trips/${tripId}/notifications/${notificationId}`
    : null;
};

export const scrollTo = (id: string, event: any) => {
  const element = document.getElementById(id);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
  event.preventDefault();
};

export const tripHasIssues = (trip: Trip) => {
  if (trip) {
    return (trip?.tripIssues?.length ?? 0) > 0;
  }
  return false;
};

export const hopHasIssues = (hop: any) => {
  if (hop) {
    return hop?.hopIssues?.length > 0;
  }
  return false;
};

export const hopHasConnectionIssues = (hop: any) => {
  if (hop) {
    return hop?.connectionIssues?.length > 0;
  }
  return false;
};

export const tripHasContacts = (contactsMap: any) => {
  return !_.isEmpty(contactsMap);
};

export const legHasIssues = (leg: Leg) => {
  if (leg?.issuesCount) {
    return leg.issuesCount > 0;
  }
  return false;
};

export const legHasFewIssues = (leg: Leg) => {
  if (leg?.issuesCount) {
    return leg.issuesCount > 0 && leg.issuesCount < 10;
  }
  return false;
};
export const legHasManyIssues = (leg: Leg) => {
  if (leg?.issuesCount) {
    return leg.issuesCount > 9;
  }
  return false;
};

export const getGoToEditItineraryUrl = (trip: Trip) => {
  return ITINERARY_ROUTE.replace(':aav1Host', getProperty('aav1Host', ''))
    .replace(':itineraryReferenceNumber', trip?.recordLocator || '')
    .replace(':firstDepartureDate', trip?.firstDepartureDate || '')
    .replace(':lastArrivalDate', trip?.lastArrivalDate || '');
};
const getGoToFlightHistoryUrl = (hop: any) => {
  const queryData = {
    carrier: hop?.airline?.code,
    flightNumber: hop?.number,
  };

  return `/flights/${hop.flightHistoryId}?${qs.stringify(queryData)}`;
};

export const goToFlightHistoryUrl = (hop: any) => {
  window.location.href = getGoToFlightHistoryUrl(hop);
};

export const travelerProfileDetails = (travelerKey: string) => {
    const agentTravelerEditUrl = '/agent/traveler/profile/searchresult/' + travelerKey;
    window.location.href = agentTravelerEditUrl;
  };

  export const getArrowClass = (leg: Leg, legHover: boolean) => {
    if (leg.hasIssues && legHover) return "sprite-arrow-orange-down";
    if (leg.hasIssues && !legHover) return "sprite-arrow-ivory-down";
    if (!leg.hasIssues && legHover) return "sprite-arrow-orange-right";
    return "sprite-arrow-ivory-right";
  };

  export const filterSpacedDMY = (dateString: string) =>
    formatDateSpacedMDY(dateString);
  export const filterDayOfWeek = (dateString: string) => formatDayOfWeek(dateString);
  export const filterStatusIndicator = (indicator: string) =>
    flightStatusIndicatorStyle(indicator);
  export const filterDepAirportCodeCityStateCountry = (flight: Flight) => departureAirportCodeCityStateCountry(flight);
  export const filterArrivalAirportCodeCityStateCountry = (flight: Flight) => arrivalAirportCodeCityStateCountry(flight);
  export const filterFlightTimeLabelStr = (
    hop: Hop,
    depArr: DepArr,
    property: string,
  ) => flightTimeLabelStr(hop, depArr, property);
  export const filterFlightTimeValue = (
    hop: Hop,
    depArr: DepArr,
    property: string,
  ) => flightTimeValue(hop, depArr, property);

